<template>
  <div>
    <v-container v-if="!loadingPage">
      <v-card style="width: 100%" elevation="2">
        <v-card-title class="headline">
          {{ patientFormSign.formSign.name }}
        </v-card-title>
        <v-card-text>
          <v-expand-transition>
            <div v-if="pdfLoaded" class="d-flex justify-center">
              <div ref="pdfPreview" class="pdf-preview"></div>
            </div>
            <v-progress-circular
              v-else
              indeterminate
              color="primary"
              class="mt-4"
            ></v-progress-circular>
          </v-expand-transition>
        </v-card-text>
      </v-card>

      <form class="pa-4">
        <v-row v-if="forms && forms.length" class="mb-4" justify="center">
          <v-col
            v-for="(form, formIndex) in forms"
            :key="formIndex"
            cols="12"
            sm="10"
            md="8"
            lg="8"
            class="py-3"
          >
            <v-card v-if="form.type === 'sign'" class="signature-card" outlined>
              <v-card-title class="signature-card-title py-2 px-4">
                <v-icon left color="primary" size="20">mdi-draw</v-icon>
                <span class="subtitle-2 font-weight-medium">{{
                  form.label
                }}</span>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-3">
                <div class="signature-box-wrapper">
                  <div class="signature-box tablet-touch-friendly">
                    <canvas :ref="'signatureCanvas-' + formIndex"></canvas>
                  </div>
                  <div class="signature-hint">
                    <v-icon x-small color="grey">mdi-gesture</v-icon>
                    <span>Draw your signature above or upload an image</span>
                  </div>
                </div>
                <div class="signature-actions">
                  <v-btn
                    @click="clearSignature(formIndex)"
                    outlined
                    color="error"
                    class="signature-action-btn"
                  >
                    <v-icon left size="18">mdi-eraser</v-icon>
                    Clear
                  </v-btn>
                  <v-btn
                    @click="triggerSignatureUpload(formIndex)"
                    outlined
                    color="primary"
                    class="signature-action-btn"
                  >
                    <v-icon left size="18">mdi-image-plus</v-icon>
                    Upload Image
                  </v-btn>
                  <input
                    type="file"
                    :ref="'signatureFileInput-' + formIndex"
                    accept="image/png,image/jpeg,image/jpg"
                    style="display: none"
                    @change="onSignatureFileChange(formIndex, $event)"
                  />
                </div>
              </v-card-text>
            </v-card>
            <template v-else-if="form.type === 'date'">
              <v-menu
                v-model="form.menu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.value"
                    :label="form.label"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    v-bind="attrs"
                    v-on="on"
                    class="professional-input"
                    hide-details="auto"
                    color="primary"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.value"
                  @input="form.menu = false"
                  color="primary"
                ></v-date-picker>
              </v-menu>
            </template>

            <v-text-field
              v-else
              :label="form.label"
              v-model="form.value"
              :type="form.type"
              outlined
              color="primary"
              class="professional-input"
              hide-details="auto"
              :append-icon="
                form.type === 'email'
                  ? 'mdi-email'
                  : form.type === 'tel'
                  ? 'mdi-phone'
                  : null
              "
            >
            </v-text-field>
          </v-col>
        </v-row>

        <v-row class="mt-6" justify="center">
          <v-col cols="12" sm="10" md="8" lg="6">
            <div class="action-buttons">
              <v-btn
                @click="editPatientFormSign"
                dark
                color="#ae1431"
                elevation="2"
                large
                class="save-btn action-btn"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Save
              </v-btn>
              <v-btn
                outlined
                color="grey darken-2"
                elevation="2"
                @click="cancelHandler"
                large
                class="cancel-btn action-btn"
              >
                <v-icon left>mdi-close-circle</v-icon>
                Cancel
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </form>
      <br />
      <br />
      <br />
      <br />
    </v-container>
    <v-progress-circular
      v-if="loadingPage"
      color="primary"
      indeterminate
      class="loading-indicator"
    ></v-progress-circular>
  </div>
</template>

<script>
import { PDFDocument } from "pdf-lib";
import SignaturePad from "signature_pad";
import * as pdfjsLib from "pdfjs-dist";
import apolloClient from "@/apollo/apollo";
import gql from "graphql-tag";
import _ from "lodash";

export default {
  data() {
    return {
      loadingPage: false,
      errorMessages: null,
      forms: [],
      patientFormSign: null,
      pdfDoc: null,
      signaturePads: {},
      pdfLoaded: false,
      namePage: 1,
      totalPages: 0,
    };
  },

  async mounted() {
    try {
      pdfjsLib.GlobalWorkerOptions.workerSrc = await import(
        "pdfjs-dist/build/pdf.worker.mjs"
      );
    } catch (e) {
      console.error("Error setting up PDF.js worker:", e);
    }

    try {
      const response = await apolloClient.query({
        query: gql`
          query findOnePatientFormSignByPatientFormSignId(
            $patientFormSignId: Int!
          ) {
            findOnePatientFormSignByPatientFormSignId(
              patientFormSignId: $patientFormSignId
            ) {
              id
              formJsonAnswers {
                name
                value
              }
              formSign {
                id
                name
                formJsonQuestion {
                  name
                  type
                  label
                  order
                  section
                }
              }
            }
          }
        `,
        variables: {
          patientFormSignId: +this.$route.params.patientFormSignId,
        },
        context: {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        },
      });

      if (
        response.data &&
        response.data.findOnePatientFormSignByPatientFormSignId
      ) {
        this.patientFormSign =
          response.data.findOnePatientFormSignByPatientFormSignId;

        let answersMap = new Map();
        try {
          if (
            this.patientFormSign.formJsonAnswers &&
            Array.isArray(this.patientFormSign.formJsonAnswers)
          ) {
            answersMap = new Map(
              this.patientFormSign.formJsonAnswers.map((answer) => [
                answer.name,
                answer,
              ])
            );
          }
        } catch (e) {
          console.error("Error creating answersMap:", e);
        }

        const formItems = this.patientFormSign.formSign.formJsonQuestion.map(
          (item) => {
            const answer = answersMap.get(item.name);

            let value = answer ? answer.value : null;
            // Fix signature display - backend stores raw base64 without data URL prefix
            if (
              item.type === "sign" &&
              value &&
              !value.startsWith("data:image")
            ) {
              value = "data:image/png;base64," + value;
            }
            // Set default current date for date inputs if no value exists
            if (item.type === "date" && !value) {
              const today = new Date();
              value = today.toISOString().split("T")[0]; // Format: YYYY-MM-DD
            }
            return {
              name: item.name,
              value: value,
              type: item.type,
              label: item.label,
              section: item.section,
              order: item.order,
            };
          }
        );

        // Sort by order if available, then use items directly without section grouping
        this.forms = formItems
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((f) => ({ ...f, menu: false })); // Add menu state for date pickers

        // Initialize signatureTabs and signatureFiles based on the number of signature fields
        // this.signatureTabs = Array(this.forms.filter(f => f.type === 'sign').length).fill(0);
        // this.signatureFiles = Array(this.forms.filter(f => f.type === 'sign').length).fill(null);

        console.log("Processed forms:", JSON.parse(JSON.stringify(this.forms)));

        // Auto-fill patient name from patient record
        try {
          const patientId = this.$route.params.id;
          if (patientId) {
            const patientResponse = await apolloClient.query({
              query: gql`
                query findOnePatientById($id: Int!) {
                  findOnePatientById(id: $id) {
                    id
                    nationalId
                    contact {
                      firstName
                      lastName
                    }
                  }
                }
              `,
              variables: { id: +patientId },
              context: {
                headers: {
                  Authorization: "Bearer " + localStorage.getItem("accessToken"),
                },
              },
            });

            const patient = patientResponse.data?.findOnePatientById;
            if (patient) {
              const fullName = [patient.contact?.firstName, patient.contact?.lastName].filter(Boolean).join(" ");
              // Auto-fill patientName and nationalId fields
              this.forms.forEach((item) => {
                if (item.name === "patientName" && !item.value) {
                  item.value = fullName;
                }
                if (item.name === "nationalId" && !item.value && patient.nationalId) {
                  item.value = patient.nationalId;
                }
              });
            }
          }
        } catch (patientError) {
          console.error("Failed to fetch patient data for auto-fill:", patientError);
        }

        this.$nextTick(() => {
          this.loadPDF();
          this.initializeSignaturePads();
        });
      } else {
        throw new Error("No data returned from the query");
      }
    } catch (error) {
      console.error("Failed to fetch form sign:", error);
      this.errorMessages = `Failed to fetch form sign: ${error.message}`;
    }
  },
  methods: {
    cancelHandler() {
      this.$router.back();
    },
    async editPatientFormSign() {
      try {
        this.loadingPage = true;

        // Transform flat forms array back to grouped structure for store compatibility
        const groupedForms = this.forms.reduce((acc, form) => {
          const existingSection = acc.find(
            (section) => section.section === form.section
          );
          if (existingSection) {
            existingSection.items.push(form);
          } else {
            acc.push({
              section: form.section,
              items: [form],
            });
          }
          return acc;
        }, []);

        const result = await this.$store.dispatch("editPatientFormSign", {
          forms: groupedForms,
          patientFormSignId: this.patientFormSign.id,
        });

        this.loadingPage = false;

        if (result.success && result.pdfBase64) {
          // Open PDF preview in new tab with download option
          this.openPdfPreview(result.pdfBase64);
        } else if (result.success) {
          // If no PDF returned, just close the window
          window.close();
        } else {
          throw new Error("Failed to edit patient form sign");
        }
      } catch (error) {
        console.error("Error editing patient form sign:", error);
        this.errorMessages = `Failed to edit patient form sign: ${error.message}`;
        this.loadingPage = false;
      }
    },
    openPdfPreview(pdfBase64) {
      // Convert base64 to blob
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const filename = `${this.patientFormSign.formSign.name.replace(
        /[\\/:*?"<>|]/g,
        "_"
      )}.pdf`;

      // Open preview window with download button
      const previewWindow = window.open("", "_blank");
      if (previewWindow) {
        const html = `<!doctype html>
<html><head>
  <meta charset="utf-8" />
  <title>${filename}</title>
  <style>
    html, body { height: 100%; margin: 0; background: #f7f7f7; }
    .toolbar { position: fixed; top: 0; left: 0; right: 0; background: #fff; padding: 8px 12px; box-shadow: 0 1px 3px rgba(0,0,0,.1); z-index: 1; display: flex; gap: 8px; align-items: center; }
    .btn { background:#10938F; color:#fff; padding:6px 12px; border-radius:4px; text-decoration:none; font: 14px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; cursor: pointer; border: none; }
    .btn:hover { background:#0d7a78; }
    .btn-close { background:#ae1431; }
    .btn-close:hover { background:#8a1027; }
    .viewer { position: absolute; top: 44px; left: 0; right: 0; bottom: 0; border: 0; width: 100%; height: calc(100% - 44px); }
  </style>
</head>
<body>
  <div class="toolbar">
    <a href="${url}" download="${filename}" class="btn">Download PDF</a>
    <button onclick="window.close()" class="btn btn-close">Close</button>
  </div>
  <iframe class="viewer" src="${url}"></iframe>
  <script>
    window.addEventListener('beforeunload', function(){ try { URL.revokeObjectURL('${url}'); } catch(e){} });
  <\/script>
</body></html>`;

        previewWindow.document.open();
        previewWindow.document.write(html);
        previewWindow.document.close();
      } else {
        // Fallback: download directly if popup blocked
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 2000);
      }
    },
    async loadPDF() {
      try {
        // Use REST API endpoint to download PDF with domain from environment variables
        const patientFormSignId = this.patientFormSign.id;
        const formSignId = this.patientFormSign.formSign.id;
        const apiDomain = process.env.VUE_APP_API_URL || "";

        // Try patient-form-sign first, fallback to form-sign template if empty
        let apiUrl = `${apiDomain}/api/patient-form-sign/${patientFormSignId}/download-pdf`;
        console.log(
          "Attempting to load PDF from patient-form-sign API:",
          apiUrl
        );

        let response = await fetch(apiUrl, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        });

        let arrayBuffer = null;

        // Check if patient-form-sign PDF is available
        if (response.ok) {
          arrayBuffer = await response.arrayBuffer();
        }

        // If patient-form-sign PDF is empty or not found, fallback to form-sign template
        if (!response.ok || !arrayBuffer || arrayBuffer.byteLength === 0) {
          console.log(
            "Patient form sign PDF not available, falling back to form-sign template"
          );
          apiUrl = `${apiDomain}/api/form-sign/${formSignId}/download-pdf`;
          console.log("Attempting to load PDF from form-sign API:", apiUrl);

          response = await fetch(apiUrl, {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("accessToken"),
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          arrayBuffer = await response.arrayBuffer();
          if (!arrayBuffer || arrayBuffer.byteLength === 0) {
            throw new Error("The PDF file is empty");
          }
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/pdf")) {
          console.warn(
            "The content type is not PDF. Content-Type:",
            contentType
          );
        }

        console.log("PDF file size:", arrayBuffer.byteLength, "bytes");

        try {
          this.pdfDoc = await PDFDocument.load(arrayBuffer);
        } catch (pdfLibError) {
          console.error("pdf-lib failed to load the PDF:", pdfLibError);

          const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
          const pdf = await loadingTask.promise;
          console.log("PDF loaded successfully with pdf.js");
          this.totalPages = pdf.numPages;
          this.pdfLoaded = true;
          this.$nextTick(() => {
            this.renderPDF(pdf);
          });
          return;
        }

        // If pdf-lib loaded successfully, also load with pdf.js for rendering
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdfJsDoc = await loadingTask.promise;
        this.pdfLoaded = true;
        this.totalPages = pdfJsDoc.numPages;
        this.$nextTick(() => {
          this.renderPDF(pdfJsDoc);
        });
      } catch (error) {
        console.error("Failed to load PDF:", error);
        this.errorMessages = `Failed to load PDF: ${error.message}`;
      }
    },

    async renderPDF(pdfDoc = null) {
      try {
        if (!pdfDoc) {
          throw new Error("PDF document must be provided");
        }

        this.totalPages = pdfDoc.numPages;
        const pdfPreview = this.$refs.pdfPreview;
        if (!pdfPreview) {
          throw new Error("PDF preview container not found");
        }

        pdfPreview.innerHTML = "";

        const pixelRatio = window.devicePixelRatio || 1;

        for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
          try {
            const page = await pdfDoc.getPage(pageNum);
            const baseViewport = page.getViewport({ scale: 1.0 });

            const containerWidth = pdfPreview.clientWidth || window.innerWidth - 48;
            const fitScale = containerWidth / baseViewport.width;

            const renderScale = Math.max(3.0, fitScale * pixelRatio);
            const viewport = page.getViewport({ scale: renderScale });

            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (!context) {
              throw new Error(`Failed to get 2D context for page ${pageNum}`);
            }

            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);

            canvas.style.width = Math.floor(baseViewport.width * fitScale) + "px";
            canvas.style.height = Math.floor(baseViewport.height * fitScale) + "px";

            await page.render({ canvasContext: context, viewport: viewport });

            const pageContainer = document.createElement("div");
            pageContainer.className = "pdf-page";
            pageContainer.appendChild(canvas);

            const pageNumber = document.createElement("div");
            pageNumber.className = "page-number";
            pageNumber.textContent = `Page ${pageNum}`;
            pageContainer.appendChild(pageNumber);
            pdfPreview.appendChild(pageContainer);
          } catch (pageError) {
            console.error(`Error rendering page ${pageNum}:`, pageError);
          }
        }
      } catch (error) {
        console.error("Failed to render PDF:", error);
        this.errorMessages = `Failed to render PDF: ${error.message}`;
      }
    },
    initializeSignaturePads() {
      this.forms.forEach((form, formIndex) => {
        if (form.type === "sign") {
          const refValue = this.$refs[`signatureCanvas-${formIndex}`];
          // Handle both array (v-for) and single element refs
          const canvasRef = Array.isArray(refValue) ? refValue[0] : refValue;
          if (canvasRef) {
            // Set canvas dimensions from its container before initializing SignaturePad
            const container = canvasRef.parentElement;
            if (container) {
              const rect = container.getBoundingClientRect();
              canvasRef.width = rect.width - 20; // Account for padding
              canvasRef.height = rect.height - 20;
            }

            const signaturePad = new SignaturePad(canvasRef, {
              minWidth: 1,
              maxWidth: 2,
              penColor: "rgb(0, 0, 0)",
            });

            // Use SignaturePad's built-in event handling for reliable stroke capture
            // Support both newer (addEventListener) and older (onEnd) versions
            if (typeof signaturePad.addEventListener === "function") {
              signaturePad.addEventListener("endStroke", () => {
                this.onSignatureEnd(formIndex, signaturePad);
              });
            } else {
              // Fallback for older signature_pad versions
              signaturePad.onEnd = () => {
                this.onSignatureEnd(formIndex, signaturePad);
              };
            }

            this.signaturePads[`${formIndex}`] = signaturePad;

            // Load existing signature if present - defer to ensure canvas is fully ready
            if (form.value) {
              this.$nextTick(() => {
                this.loadSignatureImage(signaturePad, form.value).catch(
                  (error) => {
                    console.warn("Could not load existing signature:", error);
                  }
                );
              });
            }
          }
        }
      });
    },

    onSignatureEnd(formIndex, signaturePad) {
      if (!signaturePad.isEmpty()) {
        this.forms[formIndex].value = signaturePad.toDataURL();
        console.log("Signature ended:", this.forms[formIndex].value);
      }
    },
    clearSignature(formIndex) {
      const signaturePad = this.signaturePads[`${formIndex}`];
      if (signaturePad) {
        signaturePad.clear();
        this.forms[formIndex].value = null;
      }
      // Also clear the file input if any
      const refValue = this.$refs[`signatureFileInput-${formIndex}`];
      const fileInput = Array.isArray(refValue) ? refValue[0] : refValue;
      if (fileInput) {
        fileInput.value = "";
      }
    },
    triggerSignatureUpload(formIndex) {
      const refValue = this.$refs[`signatureFileInput-${formIndex}`];
      const fileInput = Array.isArray(refValue) ? refValue[0] : refValue;
      if (fileInput) {
        fileInput.click();
      }
    },
    onSignatureFileChange(formIndex, event) {
      const file = event.target.files && event.target.files[0];
      if (!file) {
        return;
      }

      // Validate file type
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        this.errorMessages = "Please upload a PNG or JPEG image.";
        console.error("Invalid file type:", file.type);
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.errorMessages = "File size must be less than 5MB.";
        console.error("File too large:", file.size);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        const signaturePad = this.signaturePads[`${formIndex}`];
        if (signaturePad) {
          this.loadSignatureImage(signaturePad, dataUrl)
            .then(() => {
              this.forms[formIndex].value = dataUrl;
              console.log("Signature image uploaded successfully");
            })
            .catch((error) => {
              this.errorMessages = "Failed to load signature image.";
              console.error("Error loading signature image:", error);
            });
        }
      };
      reader.onerror = () => {
        this.errorMessages = "Failed to read file.";
        console.error("FileReader error");
      };
      reader.readAsDataURL(file);
    },
    loadSignatureImage(signaturePad, dataUrl) {
      return new Promise((resolve, reject) => {
        if (!signaturePad) {
          console.error("SignaturePad is not available");
          reject("SignaturePad not available");
          return;
        }

        // Handle different SignaturePad versions - canvas can be _canvas or canvas
        const canvas = signaturePad._canvas || signaturePad.canvas;
        if (!canvas) {
          console.error("Canvas is not available on SignaturePad");
          reject("Canvas not available");
          return;
        }

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          console.error("Unable to get 2D context from canvas");
          reject("Context not available");
          return;
        }

        const image = new Image();
        image.onload = () => {
          signaturePad.clear(); // Clear existing signature
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
          // Handle different SignaturePad versions for isEmpty flag
          if (typeof signaturePad._isEmpty !== "undefined") {
            signaturePad._isEmpty = false;
          }
          resolve();
        };
        image.onerror = () => {
          console.error("Failed to load signature image");
          reject("Image load failed");
        };
        image.src = dataUrl;
      });
    },
  },
};
</script>

<style scoped>
.pdf-preview {
  width: 100%;
  max-width: 800px;
  overflow-x: hidden;
}

.pdf-page {
  margin-bottom: 16px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.pdf-page:last-child {
  margin-bottom: 0;
}

.pdf-page canvas {
  display: block;
  width: 100%;
  height: auto;
}

.page-number {
  text-align: center !important;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 12px;
}

/* Signature Card Styling */
.signature-card {
  border-radius: 12px !important;
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.signature-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.signature-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 48px;
}

.signature-box-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.signature-box {
  border: 2px dashed #bdbdbd;
  border-radius: 8px;
  padding: 8px;
  width: 100%;
  height: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
  cursor: crosshair;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.signature-box:hover {
  border-color: #1976d2;
  background: linear-gradient(180deg, #ffffff 0%, #f5f9ff 100%);
}

.signature-box canvas {
  border-radius: 4px;
}

.signature-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  color: #9e9e9e;
  font-size: 12px;
  font-style: italic;
}

.signature-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  justify-content: center;
}

.signature-action-btn {
  flex: 1;
  max-width: 180px;
  height: 40px !important;
  font-weight: 500;
  letter-spacing: 0.3px;
  border-radius: 8px !important;
}

/* Professional Input Styling */
.professional-input {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.professional-input:hover {
  transform: translateY(-1px);
}

.professional-input >>> .v-input__slot {
  min-height: 56px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02) !important;
}

.professional-input >>> .v-label {
  font-size: 1rem;
  letter-spacing: 0.009375em;
}

.professional-input >>> input {
  font-size: 1rem;
  font-weight: 500;
  color: #2c3e50;
}

/* Tablet and responsive styling */
@media (max-width: 1200px) {
  .tablet-input {
    max-width: 100%;
  }

  .tablet-touch-friendly {
    max-width: 100%;
    height: 180px;
  }

  .signature-box {
    height: 180px;
  }
}

/* Tablet specific (600px - 1024px) */
@media (min-width: 600px) and (max-width: 1024px) {
  .signature-card {
    margin-bottom: 16px;
  }

  .signature-box {
    height: 200px;
    border-width: 3px;
  }

  .signature-action-btn {
    height: 48px !important;
    font-size: 15px;
    max-width: 200px;
  }

  .signature-hint {
    font-size: 13px;
    margin-top: 12px;
  }

  .v-text-field__input {
    font-size: 16px;
    padding: 14px;
  }
}

/* Mobile Specific (< 600px) */
@media (max-width: 599px) {
  form {
    padding: 8px !important;
  }

  .signature-card {
    margin-bottom: 12px;
  }

  .signature-card-title {
    padding: 12px 16px !important;
  }

  .signature-box {
    height: 180px;
    border-width: 3px;
  }

  .signature-actions {
    flex-direction: column;
    gap: 10px;
  }

  .signature-action-btn {
    max-width: 100%;
    height: 50px !important;
    font-size: 15px;
  }

  .signature-hint {
    font-size: 11px;
    text-align: center;
  }

  .v-text-field {
    margin-bottom: 16px;
  }

  .v-text-field__input {
    font-size: 16px; /* Prevent iOS zoom on input focus */
    padding: 12px;
  }

  .v-label {
    font-size: 14px;
  }

  .tablet-touch-friendly canvas {
    touch-action: manipulation;
  }
}

/* Large tablets and up (> 1024px) */
@media (min-width: 1025px) {
  .signature-action-btn {
    max-width: 160px;
  }
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.action-btn {
  flex: 1;
  max-width: 200px;
  border-radius: 8px !important;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.save-btn {
  background-color: #ae1431 !important;
}

.cancel-btn {
  background-color: transparent !important;
}

@media (max-width: 599px) {
  .action-buttons {
    flex-direction: column;
    gap: 12px;
  }

  .action-btn {
    max-width: 100%;
    height: 50px !important;
  }
}

.v-card__title {
  background-color: #a8e3e1;
}
</style>
